import { useHistoryPage } from '../hooks/useHistoryPage';
import { HistoryControls } from './shared/HistoryControls';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import trashIcon from '../assets/icons/trash.svg';
import documentIcon from '../assets/icons/document.svg';
import editIcon from '../assets/icons/edit.svg';
import { Container, Row, Col, Card, Button, Alert, Table } from 'react-bootstrap';
import { CustomerLink, VehicleLink, PaymentLink } from './EntityLinks';
import { TransactionForm } from './TransactionForm';
import { DeleteConfirmModal } from './shared/DeleteConfirmModal';
import { formatDate } from '../utils/dateFormatter';
import { formatPaymentMethod } from '../utils/paymentMethodFormatter';

/**
 * TransactionDetail component displays and allows viewing/deleting/editing of a specific transaction
 */
export function TransactionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { transactions, setTransactions, apiCall, user } = useOutletContext();
  const [transaction, setTransaction] = useState(null);
  const paymentHistory = useHistoryPage(apiCall, `/payments?transaction_id=${id}&summary=true`);
  const linkedPayments = paymentHistory.data;
  const paymentsError = paymentHistory.error;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const loadTransaction = async () => {
      setLoading(true);
      setError(null);

      try {
        const transactionId = parseInt(id);
        let foundTransaction = transactions.find(t => t.id === transactionId);

        if (!foundTransaction) {
          const response = await apiCall(`${API_BASE_URL}/transactions/${transactionId}`);
          foundTransaction = response.data || response;
        }

        if (foundTransaction) {
          setTransaction(foundTransaction);

        } else {
          setError('Transaction not found');
        }
      } catch (err) {
        setError('Failed to load transaction: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    loadTransaction();
  }, [id, transactions, apiCall]);

  const handleDelete = () => {
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await apiCall(`${API_BASE_URL}/transactions/${transaction.id}`, { method: 'DELETE' });
      setShowDeleteModal(false);
      const transactionsRes = await apiCall(`${API_BASE_URL}/transactions`);
      setTransactions(transactionsRes?.data || []);
      navigate('/dashboard/transactions');
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleResendInvoice = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiCall(
        `${API_BASE_URL}/transactions/${transaction.id}/resend-invoice`,
        { method: 'POST' }
      );

      alert(response.data?.message || 'Invoice sent successfully!');
    } catch (err) {
      setError('Failed to send invoice: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateContract = async () => {
    if (!transaction.vehicle_id) {
      alert('Cannot generate contract: Transaction must have a vehicle');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiCall(
        `${API_BASE_URL}/transactions/${transaction.id}/generate-contract`,
        { method: 'POST' }
      );

      // Decode base64 PDF and trigger download
      const base64PDF = response.data.pdfBase64;
      const binaryString = window.atob(base64PDF);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = response.data.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      alert('Contract generated and downloaded successfully!');
    } catch (err) {
      setError('Failed to generate contract: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (formData) => {
    setError(null);

    try {
      // Calculate end_date for rentals
      let dataToSend = { ...formData };

      if (formData.type === 'rental' && formData.start_date && formData.quantity) {
        const startDate = new Date(formData.start_date);
        const endDate = new Date(startDate);
        const quantity = parseInt(formData.quantity);

        if (formData.unit === 'day') {
          endDate.setDate(endDate.getDate() + quantity);
        } else if (formData.unit === 'week') {
          endDate.setDate(endDate.getDate() + quantity * 7);
        } else if (formData.unit === 'month') {
          endDate.setMonth(endDate.getMonth() + quantity);
        }

        dataToSend.end_date = endDate.toISOString().split('T')[0];

        // Calculate daily_rate from amount
        let vehicle = null;
        try {
          const vRes = await apiCall(`${API_BASE_URL}/vehicles/${formData.vehicle_id}`);
          vehicle = vRes?.data;
        } catch {}
        if (vehicle) {
          const pricingTiers = {
            'basic_economy': { daily: 45, weekly: 200, monthly: 700 },
            'full_size_sedan': { daily: 55, weekly: 250, monthly: 800 },
            'newer_sedan_suv': { daily: 65, weekly: 280, monthly: 900 },
            'luxury': { daily: 75, weekly: 350, monthly: 1100 }
          };
          const pricing = pricingTiers[vehicle.class];
          if (pricing) {
            if (formData.unit === 'day') {
              dataToSend.daily_rate = pricing.daily;
            } else if (formData.unit === 'week') {
              dataToSend.daily_rate = pricing.weekly;
            } else if (formData.unit === 'month') {
              dataToSend.daily_rate = pricing.monthly;
            }
          }
        }
      } else if (formData.type === 'fee') {
        dataToSend.end_date = formData.start_date;
        dataToSend.daily_rate = 0;
        dataToSend.insurance_id = null;
        if (!dataToSend.vehicle_id) {
          dataToSend.vehicle_id = null;
        }
      }

      // Remove fields that aren't part of the update payload
      delete dataToSend.isPaid;
      delete dataToSend.payment_method;
      delete dataToSend.unit;
      delete dataToSend.quantity;

      const response = await apiCall(
        `${API_BASE_URL}/transactions/${transaction.id}`,
        {
          method: 'PUT',
          body: JSON.stringify(dataToSend),
        }
      );

      alert('Transaction updated successfully');

      // Reload transactions and update state
      const transactionsRes = await apiCall(`${API_BASE_URL}/transactions`);
      setTransactions(transactionsRes?.data || []);

      // Update current transaction
      const updatedTransaction = response.data;
      setTransaction(updatedTransaction);
      setIsEditing(false);
    } catch (err) {
      throw new Error(err.message || 'Failed to update transaction');
    }
  };

  // Use joined data from API response
  const getCustomerName = (trans) => {
    if (trans.first_name && trans.last_name) {
      return `${trans.first_name} ${trans.last_name}`;
    }
    return `Customer #${trans.customer_id}`;
  };

  const getVehicleInfo = (trans) => {
    if (trans.make && trans.model) {
      return `${trans.year || ''} ${trans.make} ${trans.model}`.trim();
    }
    return `Vehicle #${trans.vehicle_id}`;
  };

  if (loading) {
    return (
      <Container fluid className="py-4">
        <div>Loading transaction details...</div>
      </Container>
    );
  }

  if (error && !transaction) {
    return (
      <Container fluid className="py-4">
        <Button variant="link" onClick={() => navigate('/dashboard/transactions')} className="p-0">
          ← Back to Transactions
        </Button>
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4 domain-transactions">
      <Row className="mb-3">
        <Col>
          <Button variant="link" onClick={() => navigate('/dashboard/transactions')} className="p-0">
            ← Back to Transactions
          </Button>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <h2>
            Transaction #{transaction.id}
            <span style={{ fontSize: '0.8em', marginLeft: '10px', color: '#666' }}>
              ({transaction.type.toUpperCase()})
            </span>
          </h2>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      {isEditing ? (
        <Card className="mb-3">
          <Card.Header>
            <h3>Edit Transaction</h3>
          </Card.Header>
          <Card.Body>
            <TransactionForm
              initialData={{
                customer_id: transaction.customer_id || '',
                type: transaction.type || 'rental',
                store_id: transaction.store_id?.toString() || '',
                vehicle_id: transaction.vehicle_id || '',
                insurance_id: transaction.insurance_id?.toString() || '',
                start_date: transaction.start_date?.split('T')[0] || '',
                amount: transaction.amount?.toString() || '',
                notes: transaction.notes || '',
              }}
              onSubmit={handleEditSubmit}
              onCancel={() => setIsEditing(false)}
              apiCall={apiCall}
              submitLabel="Update Transaction"
            />
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Card.Header>
            <h3>Transaction Information</h3>
          </Card.Header>
          <Card.Body>
          <Row>
            <Col md={6} className="mb-3">
              <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Type</strong>
              <span>{transaction.type.toUpperCase()}</span>
            </Col>
            <Col md={6} className="mb-3">
              <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Customer</strong>
              <CustomerLink
                customerId={transaction.customer_id}
                customerName={getCustomerName(transaction)}
              />
            </Col>
            {transaction.vehicle_id && (
              <Col md={6} className="mb-3">
                <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Vehicle</strong>
                <VehicleLink
                  vehicleId={transaction.vehicle_id}
                  vehicleInfo={getVehicleInfo(transaction)}
                />
              </Col>
            )}
            <Col md={6} className="mb-3">
              <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Amount</strong>
              <span>${(parseFloat(transaction.amount) + parseFloat(transaction.tax_amount || 0)).toFixed(2)}</span>
              {parseFloat(transaction.tax_amount || 0) > 0 && (
                <small className="d-block" style={{ color: '#e67e22' }}>
                  (Rental: ${parseFloat(transaction.amount).toFixed(2)} + Tax: ${parseFloat(transaction.tax_amount).toFixed(2)})
                </small>
              )}
            </Col>
            <Col md={6} className="mb-3">
              <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Start Date</strong>
              <span>{transaction.start_date?.split('T')[0] || '-'}</span>
            </Col>
            <Col md={6} className="mb-3">
              <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>End Date</strong>
              <span>{transaction.end_date?.split('T')[0] || '-'}</span>
            </Col>
            {transaction.daily_rate && (
              <Col md={6} className="mb-3">
                <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Daily Rate</strong>
                <span>${transaction.daily_rate}</span>
              </Col>
            )}
            {transaction.insurance_id && (
              <Col md={6} className="mb-3">
                <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Insurance ID</strong>
                <span>{transaction.insurance_id}</span>
              </Col>
            )}
            {transaction.notes && (
              <Col md={12} className="mb-3">
                <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Notes</strong>
                <span>{transaction.notes}</span>
              </Col>
            )}
            <Col md={6} className="mb-3">
              <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Created By</strong>
              <span>{transaction.agent_name ? `${transaction.agent_name} (ID: ${transaction.agent_id})` : `Agent ID: ${transaction.agent_id}`}</span>
            </Col>
          </Row>
          <div className="mt-3">
            <Button
              variant="primary"
              onClick={handleGenerateContract}
              disabled={loading || !transaction.vehicle_id}
              className="me-2"
            >
              <img src={documentIcon} alt="" className="icon-btn" />
              Generate Contract
            </Button>
            <Button
              variant="info"
              onClick={handleResendInvoice}
              disabled={loading}
              className="me-2"
            >
              📧 Resend Invoice
            </Button>
            {user?.role !== 'auditor' && (
              <>
                <Button
                  variant="warning"
                  onClick={() => setIsEditing(true)}
                  className="me-2"
                >
                  <img src={editIcon} alt="" className="icon-btn" />
                  Edit
                </Button>
                <Button
                  variant="danger"
                  onClick={handleDelete}
                >
                  <img src={trashIcon} alt="" className="icon-btn" />
                  Delete Transaction
                </Button>
              </>
            )}
          </div>
        </Card.Body>
      </Card>
      )}

      {!isEditing && (
        <Card className="mt-3">
          <Card.Header>
            <h3>Payments ({paymentHistory.total || 0})</h3>
            <HistoryControls history={paymentHistory} label="payments" />
            {paymentHistory.summary && <div>Total received: ${Number(paymentHistory.summary.total_paid).toFixed(2)} · Discounts: ${Number(paymentHistory.summary.total_discount).toFixed(2)}</div>}
          </Card.Header>
          <Card.Body>
            {paymentsError ? (
              <Alert variant="warning">{paymentsError}</Alert>
            ) : linkedPayments.length > 0 ? (
              <Table striped hover responsive>
                <thead>
                  <tr>
                    <th>Payment</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Payment Method</th>
                  </tr>
                </thead>
                <tbody>
                  {linkedPayments.map(payment => (
                    <tr key={payment.id}>
                      <td>
                        <PaymentLink paymentId={payment.id}>#{payment.id}</PaymentLink>
                      </td>
                      <td>{formatDate(payment.payment_date) || '-'}</td>
                      <td>${parseFloat(payment.amount).toFixed(2)}</td>
                      <td>{formatPaymentMethod(payment.payment_method)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <div className="text-muted text-center py-3">No payments linked to this transaction</div>
            )}
          </Card.Body>
        </Card>
      )}

      <DeleteConfirmModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        itemLabel="transaction"
        error={deleteError}
        isDeleting={isDeleting}
      />
    </Container>
  );
}

export default TransactionDetail;
